/**
 * Remote console — chat with a per-module Claude Code session that runs on
 * the operator's PC (the remote_console client). One "room" per module.
 *
 *   browser (this page) ──wss(ticket)──► web-svc relay ──wss(token)──► PC
 *
 * History: the relay persists each turn to web.console_messages. On open we
 * load the latest page and show it; scrolling to the top lazy-loads older
 * pages. Live messages stream in over the websocket and append to the bottom.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHost } from '@admin-portal/host-contract';
import { navigate, withAppBasePath } from '../nav';

const ROOMS: { key: string; label: string }[] = [
  { key: 'web', label: 'Web · web-svc' },
  { key: 'order', label: 'Orders · orders-svc' },
];
const PAGE = 30;

type ToolEv = { name: string; input: string };
type Msg = {
  id: string;          // stable react key
  dbId?: number;       // set for persisted rows — the pagination cursor
  role: 'user' | 'assistant';
  text: string;
  thinking?: string;
  tools?: ToolEv[];
  working?: boolean;
  error?: string;
  cost?: number | null;
  subtype?: string | null;
};

type Conn = 'connecting' | 'open' | 'closed';

type PendingFile = { fid: string; name: string; status: 'uploading' | 'ready' | 'error'; path?: string; error?: string };

const MAX_FILE_BYTES = 4 * 1024 * 1024;

function uid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function mapHistory(rows: any[]): Msg[] {
  return rows.map((r) => {
    const m: Msg = { id: 'h' + r.id, dbId: r.id, role: r.role, text: '' };
    if (r.is_error) m.error = r.text || 'error';
    else m.text = r.text || '';
    if (r.cost != null) m.cost = r.cost;
    if (r.role === 'assistant') m.subtype = r.is_error ? 'error' : 'success';
    return m;
  });
}

export function Console({ room }: { room: string }) {
  const { fetchApi } = useHost();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [conn, setConn] = useState<Conn>('connecting');
  const [agentOnline, setAgentOnline] = useState(false);
  const [input, setInput] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [files, setFiles] = useState<PendingFile[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickBottom = useRef(true);
  const prepend = useRef<{ h: number; top: number } | null>(null);
  const oldestDbId = useRef<number | undefined>(undefined);
  const hasMoreRef = useRef(false);
  const loadingRef = useRef(false);

  const anyWorking = msgs.some((m) => m.role === 'assistant' && m.working);

  const patch = useCallback((id: string, fn: (m: Msg) => Msg) => {
    setMsgs((prev) => prev.map((m) => (m.id === id ? fn(m) : m)));
  }, []);

  const handleFrame = useCallback(
    (f: any) => {
      switch (f.t) {
        case 'agent_status':
          setAgentOnline(!!f.online);
          break;
        case 'status':
          if (f.state === 'working' && f.id) patch(f.id, (m) => ({ ...m, working: true }));
          break;
        case 'delta':
          if (f.id) patch(f.id, (m) => ({ ...m, text: m.text + (f.text || ''), working: true }));
          break;
        case 'thinking':
          if (f.id) patch(f.id, (m) => ({ ...m, thinking: (m.thinking || '') + (f.text || '') }));
          break;
        case 'tool':
          if (f.id)
            patch(f.id, (m) => ({
              ...m,
              tools: [...(m.tools || []), { name: f.name, input: f.input }],
              working: true,
            }));
          break;
        case 'turn_done':
          if (f.id) patch(f.id, (m) => ({ ...m, working: false, cost: f.cost, subtype: f.subtype }));
          break;
        case 'turn_error':
          if (f.id) patch(f.id, (m) => ({ ...m, working: false, error: f.error || 'error' }));
          break;
        case 'file_saved':
          setFiles((prev) => prev.map((x) => (x.fid === f.fid ? { ...x, status: 'ready', path: f.path } : x)));
          break;
        case 'file_error':
          setFiles((prev) => prev.map((x) => (x.fid === f.fid ? { ...x, status: 'error', error: f.error } : x)));
          break;
      }
    },
    [patch]
  );

  // Read a picked file, ship it to the PC agent over the live socket, and
  // track upload status as a chip above the input.
  const onPickFiles = useCallback((fileList: FileList | null) => {
    const ws = wsRef.current;
    if (!fileList || !ws || ws.readyState !== WebSocket.OPEN) return;
    Array.from(fileList).forEach((file) => {
      if (file.size > MAX_FILE_BYTES) {
        alert(`${file.name} is over 4MB — too large to upload here.`);
        return;
      }
      const fid = uid();
      setFiles((prev) => [...prev, { fid, name: file.name, status: 'uploading' }]);
      const reader = new FileReader();
      reader.onload = () => {
        const result = (reader.result as string) || '';
        const data = result.slice(result.indexOf(',') + 1);
        try {
          ws.send(JSON.stringify({
            t: 'file', fid, name: file.name,
            mime: file.type || 'application/octet-stream',
            size: file.size, data,
          }));
        } catch {
          setFiles((prev) => prev.map((x) => (x.fid === fid ? { ...x, status: 'error', error: 'send failed' } : x)));
        }
      };
      reader.onerror = () => {
        setFiles((prev) => prev.map((x) => (x.fid === fid ? { ...x, status: 'error', error: 'read failed' } : x)));
      };
      reader.readAsDataURL(file);
    });
  }, []);

  // Load older history when the user scrolls near the top.
  const loadOlder = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;
    const before = oldestDbId.current;
    if (before == null) return;
    loadingRef.current = true;
    setLoadingOlder(true);
    try {
      const res = await fetchApi(
        `/web/admin/console/history?room=${encodeURIComponent(room)}&before=${before}&limit=${PAGE}`
      );
      if (res.ok) {
        const j = await res.json();
        const older = mapHistory(j.messages || []);
        if (older.length) {
          const el = scrollRef.current;
          prepend.current = el ? { h: el.scrollHeight, top: el.scrollTop } : null;
          oldestDbId.current = older[0].dbId;
          setMsgs((prev) => [...older, ...prev]);
        }
        hasMoreRef.current = !!j.has_more;
        setHasMore(!!j.has_more);
      }
    } catch {
      /* ignore — history is best-effort */
    } finally {
      loadingRef.current = false;
      setLoadingOlder(false);
    }
  }, [room, fetchApi]);

  // Room change: reset, load latest history, then open the live socket.
  useEffect(() => {
    let closed = false;
    let ping: ReturnType<typeof setInterval> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;

    setMsgs([]);
    setAgentOnline(false);
    setHasMore(false);
    oldestDbId.current = undefined;
    hasMoreRef.current = false;
    stickBottom.current = true;
    prepend.current = null;

    (async () => {
      try {
        const res = await fetchApi(
          `/web/admin/console/history?room=${encodeURIComponent(room)}&limit=${PAGE}`
        );
        if (!res.ok || closed) return;
        const j = await res.json();
        const hist = mapHistory(j.messages || []);
        if (hist.length) oldestDbId.current = hist[0].dbId;
        hasMoreRef.current = !!j.has_more;
        setHasMore(!!j.has_more);
        stickBottom.current = true;
        setMsgs(hist);
      } catch {
        /* ignore */
      }
    })();

    async function connect() {
      if (closed) return;
      setConn('connecting');
      let ticket: string, wsUrl: string;
      try {
        const res = await fetchApi('/web/admin/console/ws-ticket', { method: 'POST' });
        if (!res.ok) throw new Error();
        const j = await res.json();
        ticket = j.ticket;
        wsUrl = j.wsUrl;
      } catch {
        retry = setTimeout(connect, 2500);
        return;
      }
      const ws = new WebSocket(
        `${wsUrl}/console/client-ws?ticket=${encodeURIComponent(ticket)}&room=${encodeURIComponent(room)}`
      );
      wsRef.current = ws;
      ws.onopen = () => {
        setConn('open');
        ping = setInterval(() => {
          try {
            ws.send(JSON.stringify({ t: 'ping' }));
          } catch {
            /* ignore */
          }
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          handleFrame(JSON.parse(ev.data));
        } catch {
          /* ignore */
        }
      };
      ws.onclose = () => {
        setConn('closed');
        if (ping) clearInterval(ping);
        if (!closed) retry = setTimeout(connect, 2500);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      };
    }
    connect();

    return () => {
      closed = true;
      if (ping) clearInterval(ping);
      if (retry) clearTimeout(retry);
      try {
        wsRef.current?.close();
      } catch {
        /* ignore */
      }
    };
  }, [room, fetchApi, handleFrame]);

  // Keep scroll sane: restore position after prepending older messages;
  // otherwise stick to the bottom when the user is already there.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (prepend.current) {
      el.scrollTop = el.scrollHeight - prepend.current.h + prepend.current.top;
      prepend.current = null;
    } else if (stickBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [msgs]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    stickBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (el.scrollTop < 60 && hasMoreRef.current && !loadingRef.current) loadOlder();
  }, [loadOlder]);

  const send = useCallback(() => {
    const text = input.trim();
    const ws = wsRef.current;
    const ready = files.filter((f) => f.status === 'ready');
    if ((!text && ready.length === 0) || !ws || ws.readyState !== WebSocket.OPEN) return;
    const id = uid();
    const attachPrefix = ready.length
      ? `[Attached file${ready.length > 1 ? 's' : ''}: ${ready.map((f) => f.path).join(', ')}]\n\n`
      : '';
    const displayText = ready.length
      ? `${ready.map((f) => `📎 ${f.name}`).join('  ')}${text ? '\n' + text : ''}`
      : text;
    stickBottom.current = true;
    setMsgs((prev) => [
      ...prev,
      { id: id + ':u', role: 'user', text: displayText },
      { id, role: 'assistant', text: '', working: true, tools: [] },
    ]);
    ws.send(JSON.stringify({ t: 'user_msg', id, text: attachPrefix + text }));
    setInput('');
    setFiles([]);
  }, [input, files]);

  const interrupt = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'interrupt' }));
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="rc-wrap">
      <style>{CSS}</style>

      <div className="rc-head">
        <div className="rc-tabs">
          {ROOMS.map((r) => (
            <a
              key={r.key}
              href={withAppBasePath(`/chat/${r.key}`)}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.button !== 0) return;
                e.preventDefault();
                navigate(`/chat/${r.key}`);
              }}
              className={`rc-tab${r.key === room ? ' active' : ''}`}
            >
              {r.label}
            </a>
          ))}
        </div>
        <div className="rc-badges">
          <span className={`rc-badge ${conn === 'open' ? 'ok' : conn === 'connecting' ? 'warn' : 'bad'}`}>
            {conn === 'open' ? 'relay ●' : conn === 'connecting' ? 'connecting…' : 'relay ○'}
          </span>
          <span className={`rc-badge ${agentOnline ? 'ok' : 'bad'}`}>
            {agentOnline ? 'PC ●' : 'PC offline'}
          </span>
        </div>
      </div>

      {!agentOnline && conn === 'open' && (
        <div className="rc-warn">
          The PC client isn’t running. Start <code>run_console_client.bat</code> on the counter PC to
          bring the <b>{room}</b> session online.
        </div>
      )}

      <div className="rc-scroll" ref={scrollRef} onScroll={onScroll}>
        {loadingOlder && <div className="rc-older">loading older…</div>}
        {!hasMore && msgs.length > 0 && <div className="rc-older">· beginning of history ·</div>}
        {msgs.length === 0 && (
          <div className="rc-empty">
            Talk to the <b>{room}</b> module’s Claude session. It can read, edit, and deploy that
            module. Messages run on the counter PC and bill Claude usage there.
          </div>
        )}
        {msgs.map((m) =>
          m.role === 'user' ? (
            <div key={m.id} className="rc-msg user">
              <div className="rc-bubble">{m.text}</div>
            </div>
          ) : (
            <div key={m.id} className="rc-msg asst">
              <div className="rc-bubble">
                {m.tools && m.tools.length > 0 && (
                  <div className="rc-tools">
                    {m.tools.map((t, i) => (
                      <div key={i} className="rc-tool" title={t.input}>
                        <b>{t.name}</b> {t.input}
                      </div>
                    ))}
                  </div>
                )}
                {m.text && <div className="rc-text">{m.text}</div>}
                {m.working && !m.text && <div className="rc-think">working…</div>}
                {m.error && <div className="rc-err">⚠ {m.error}</div>}
                {!m.working && (m.cost != null || (m.subtype && m.subtype !== 'success')) && (
                  <div className="rc-meta">
                    {m.subtype && m.subtype !== 'success' ? `${m.subtype} · ` : ''}
                    {m.cost != null ? `$${m.cost.toFixed(4)}` : ''}
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </div>

      <div className="rc-input">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            onPickFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <div className="rc-input-col">
          {files.length > 0 && (
            <div className="rc-files">
              {files.map((f) => (
                <span key={f.fid} className={`rc-file ${f.status}`}>
                  📎 {f.name}
                  {f.status === 'uploading' ? ' …' : f.status === 'error' ? ` ⚠ ${f.error || 'failed'}` : ''}
                  <button
                    type="button"
                    className="rc-file-x"
                    onClick={() => setFiles((prev) => prev.filter((x) => x.fid !== f.fid))}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={`Message the ${room} session…  (Enter to send, Shift+Enter for newline)`}
            rows={2}
          />
        </div>
        <button
          type="button"
          className="rc-btn attach"
          onClick={() => fileInputRef.current?.click()}
          disabled={conn !== 'open'}
          title="Attach a file"
        >
          📎
        </button>
        {anyWorking ? (
          <button className="rc-btn stop" onClick={interrupt}>
            Stop
          </button>
        ) : (
          <button
            className="rc-btn"
            onClick={send}
            disabled={conn !== 'open' || (!input.trim() && !files.some((f) => f.status === 'ready'))}
          >
            Send
          </button>
        )}
      </div>
    </div>
  );
}

const CSS = `
.rc-wrap{display:flex;flex-direction:column;height:100%;min-height:0;color:var(--fg)}
.rc-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 14px;border-bottom:1px solid var(--border)}
.rc-tabs{display:flex;gap:8px}
.rc-tab{padding:6px 14px;border-radius:8px;font-size:13px;text-decoration:none;color:var(--fg-muted);border:1px solid transparent}
.rc-tab.active{background:var(--bg-elev);color:var(--fg);border-color:var(--border)}
.rc-badges{display:flex;gap:8px;align-items:center}
.rc-badge{font-size:11px;padding:3px 9px;border-radius:20px;font-weight:600;letter-spacing:.02em}
.rc-badge.ok{background:#0f2e1c;color:#4ade80}
.rc-badge.warn{background:#3a2e12;color:#fbbf24}
.rc-badge.bad{background:#3a1717;color:#f87171}
.rc-warn{margin:10px 14px 0;padding:9px 12px;border-radius:8px;background:#3a2e12;color:#fbbf24;font-size:13px}
.rc-warn code{background:#0000002e;padding:1px 5px;border-radius:4px}
.rc-scroll{flex:1;min-height:0;overflow-y:auto;padding:16px 14px;display:flex;flex-direction:column;gap:12px}
.rc-older{text-align:center;color:var(--fg-muted);font-size:11.5px;padding:2px 0}
.rc-empty{color:var(--fg-muted);font-size:13px;max-width:640px;margin:auto;text-align:center;line-height:1.6}
.rc-msg{display:flex}
.rc-msg.user{justify-content:flex-end}
.rc-msg.asst{justify-content:flex-start}
.rc-bubble{max-width:82%;padding:10px 13px;border-radius:12px;font-size:13.5px;line-height:1.55}
.rc-msg.user .rc-bubble{background:#2563eb;color:#fff;border-bottom-right-radius:4px;white-space:pre-wrap;word-break:break-word}
.rc-msg.asst .rc-bubble{background:#e4e4e7;color:#15803d;border:1px solid #d4d4d8;border-bottom-left-radius:4px}
.rc-text{white-space:pre-wrap;word-break:break-word;color:#15803d}
.rc-tools{display:flex;flex-direction:column;gap:4px;margin-bottom:8px}
.rc-tool{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11.5px;color:#93c5fd;background:#0b1220;border:1px solid #1e2b45;border-radius:6px;padding:4px 8px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.rc-tool b{color:#bfdbfe}
.rc-think{color:#94a3b8;font-style:italic;font-size:12.5px}
.rc-err{color:#f87171;font-size:12.5px;margin-top:6px}
.rc-meta{color:#94a3b8;font-size:11px;margin-top:8px}
.rc-input{display:flex;align-items:flex-end;gap:8px;padding:12px 14px;border-top:1px solid var(--border)}
.rc-input-col{flex:1;min-width:0;display:flex;flex-direction:column;gap:6px}
.rc-files{display:flex;flex-wrap:wrap;gap:6px}
.rc-file{display:inline-flex;align-items:center;gap:4px;background:var(--bg-elev);border:1px solid var(--border);border-radius:20px;padding:3px 6px 3px 10px;font-size:12px;color:var(--fg)}
.rc-file.error{color:#f87171;border-color:#7f1d1d}
.rc-file-x{background:none;border:none;color:var(--fg-muted);cursor:pointer;font-size:14px;line-height:1;padding:0 2px}
.rc-input textarea{width:100%;resize:none;background:var(--bg-elev);color:var(--fg);border:1px solid var(--border);border-radius:10px;padding:10px 12px;font-size:13.5px;font-family:inherit;line-height:1.5}
.rc-input textarea::placeholder{color:var(--fg-muted)}
.rc-input textarea:focus{outline:none;border-color:#3b82f6}
.rc-btn{align-self:stretch;padding:0 20px;border-radius:10px;border:none;background:#2563eb;color:#fff;font-weight:600;font-size:13.5px;cursor:pointer}
.rc-btn:disabled{opacity:.4;cursor:not-allowed}
.rc-btn.stop{background:#b91c1c}
.rc-btn.attach{background:var(--bg-elev);color:var(--fg);border:1px solid var(--border);padding:0 16px}
`;
